import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@taxdown/zeno-sama-query';
import { UserData } from '@taxdown/kami-sama/dist/definition';
import { SamaDictionary } from '@taxdown/kami-sama/dist/kamiSama/definition';
import RecordsService from 'Services/records';
import TaxService from 'Services/tax';
import { history, store } from 'Store';
import { setStageAction } from 'Store/modules/validations/actions';
import { setIsNotLoadingAction } from 'Store/modules/app/actions';
import { setTaxSelectedAction } from 'Store/modules/tax/actions';
import { useGetQuery } from 'Hooks/useGetQuery';
import { TAX_YEAR } from 'Config';
import { routesByStage } from 'Constants/routes';
import { promiseTimeout } from 'Tests/utils';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { CustomerType, QueryKeys, RecordType, ResponseArrayType, ResponseType } from 'Types/common';

export const useFetchValidation = (): UseQueryResult<SamaDictionary> =>
  useQuery([QueryKeys.GET_FORMS], () => TaxService.getForms({ taxYear: TAX_YEAR }), {
    select: data => {
      const response = data?.data;
      return response.item;
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });

export const fetchGetRecords = async (): Promise<ResponseArrayType<RecordType> | null> => {
  const response = await RecordsService.getRecords({ current: true });
  const info = response?.data[0];
  const record = info?.item;
  const { recordId, taxYear, stage } = record;
  if (recordId) {
    store.dispatch(setTaxSelectedAction({ recordId, taxYear }));
  }
  if (stage) {
    store.dispatch(setStageAction(stage));
  }
  return response ?? null;
};

export const useFetchGetUserData = (
  isRecordIdAvailable: boolean
): UseQueryResult<UserData | undefined> =>
  useQuery(
    [QueryKeys.GET_USER_DATA],
    async () => {
      if (!isRecordIdAvailable) {
        await fetchGetRecords();
      }
      const response = await RecordsService.getUserData();
      return !response ? null : response?.data;
    },
    {
      select: data => data?.item,
      refetchInterval: data => (data ? false : 500),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

export const useResetUser = (): UseMutationResult<ResponseType<RecordType>> =>
  useMutation([QueryKeys.RESET_USER], RecordsService.resetUsers, {
    onSuccess: response => {
      const data = response?.data;
      if (data.item.stage) {
        store.dispatch(setStageAction(data?.item?.stage));
        history.replace(routesByStage[data?.item?.stage]);
      }
    },
    onSettled: () => {
      store.dispatch(setIsNotLoadingAction());
    },
  });

export const useRestartUser = (stage: string | null): UseMutationResult<unknown> =>
  useMutation(
    [QueryKeys.RESTART_USER],
    async () => {
      if (stage) {
        const response = await RecordsService.restartUsers({ stage });
        await promiseTimeout(10000);
        return response;
      }
      return null;
    },
    {
      onSuccess: response => {
        const data = response?.data;
        if (data?.item?.stage) {
          store.dispatch(setStageAction(data?.item?.stage));
        }
      },
    }
  );

export const useGetRecord = (): UseQueryResult<RecordType | undefined> =>
  useQuery(
    [QueryKeys.GET_RECORD],
    async () => {
      await fetchGetRecords();
    },
    {
      refetchInterval: record => (record?.data ? false : 500),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

export const useGetUserPlan = (): FRONT_PLANS => {
  const data = useGetQuery<ResponseType<CustomerType>>([QueryKeys.GET_CUSTOMER_INFO]);
  const customerInfo = data?.data?.item;
  const plan = customerInfo?.activePlan
    ? plansNameMapping(customerInfo?.activePlan?.name)
    : FRONT_PLANS.BASIC;
  return plan;
};
